import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import Icon from '../../../components/AppIcon';
import { clientPortalService } from '../../../services/clientPortalService';

const ProgressTracking = ({ progressData, latestScores, compact = false }) => {
  const dimensions = [
    { key: 'data_infrastructure', label: 'Data Infrastructure', color: '#3B82F6' },
    { key: 'analytics_capabilities', label: 'Analytics Capabilities', color: '#10B981' },
    { key: 'governance_maturity', label: 'Governance Maturity', color: '#8B5CF6' },
    { key: 'team_skills', label: 'Team Skills', color: '#F59E0B' },
    { key: 'strategic_alignment', label: 'Strategic Alignment', color: '#EF4444' }
  ];

  const timeSeriesData = clientPortalService?.getTimeSeriesProgressData(progressData);

  // Prepare radar chart data
  const radarData = dimensions?.map(dimension => ({
    dimension: dimension?.label?.split(' ')?.[0], // Shortened label
    score: latestScores?.[dimension?.key]?.score || 0,
    fullMark: 100
  }));

  // Get milestones
  const milestones = progressData
    ?.filter(p => p?.milestoneAchieved)
    ?.slice(0, compact ? 3 : 10)
    ?.map(p => ({
      dimension: dimensions?.find(d => d?.key === p?.dimension)?.label,
      milestone: p?.milestoneAchieved,
      date: new Date(p?.trackedAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      color: dimensions?.find(d => d?.key === p?.dimension)?.color
    }));

  if (!progressData || progressData?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#002B45]">Progress Tracking</h3>
        </div>
        <div className="text-center py-12">
          <Icon name="TrendingUp" size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No progress data available yet</p>
          <p className="text-sm text-gray-400 mt-2">Complete multiple assessments to track your progress over time</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
          <Icon name="TrendingUp" size={20} className="text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#002B45]">Progress Tracking</h3>
          <p className="text-sm text-gray-500">Data maturity evolution across all dimensions</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Time Series Chart */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-[#002B45] mb-4">Maturity Progression Over Time</h4>
          <ResponsiveContainer width="100%" height={compact ? 200 : 300}>
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload?.length) {
                    return (
                      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                        <p className="font-semibold text-[#002B45] mb-2">{payload?.[0]?.payload?.date}</p>
                        {payload?.map((entry, index) => (
                          <p key={index} className="text-sm" style={{ color: entry?.color }}>
                            {dimensions?.find(d => d?.key === entry?.dataKey)?.label}: {entry?.value}
                          </p>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              {dimensions?.map((dimension) => (
                <Line
                  key={dimension?.key}
                  type="monotone"
                  dataKey={dimension?.key}
                  stroke={dimension?.color}
                  strokeWidth={2}
                  name={dimension?.label?.split(' ')?.[0]}
                  dot={{ fill: dimension?.color, r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Radar Chart */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-[#002B45] mb-4">Current Maturity Profile</h4>
          <ResponsiveContainer width="100%" height={compact ? 200 : 300}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar
                name="Current Score"
                dataKey="score"
                stroke="#1FB5A5"
                fill="#1FB5A5"
                fillOpacity={0.6}
              />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Milestones */}
      {milestones?.length > 0 && (
        <div className="pt-6 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-[#002B45] mb-4 flex items-center gap-2">
            <Icon name="Award" size={18} className="text-[#1FB5A5]" />
            Achievement Milestones
          </h4>
          <div className="space-y-3">
            {milestones?.map((milestone, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 bg-gradient-to-r from-[#1FB5A5]/10 to-transparent rounded-lg p-3"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${milestone?.color}20` }}
                >
                  <Icon name="Trophy" size={16} style={{ color: milestone?.color }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#002B45]">{milestone?.milestone}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {milestone?.dimension} • {milestone?.date}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!compact && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">Track Your Journey</p>
              <p className="text-xs text-blue-700">
                Your progress is tracked across five key dimensions. Continue completing assessments to see your maturity evolution and unlock new milestones.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressTracking;